import React, { useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type {
  GHABlockedIdentity,
  GHABlockedIdentityDetails,
  GHABlockedIdentityEvidence,
} from '~/services/ConnectionGoogleService';

import { GHA_BLOCKED_IDENTITY_EVIDENCE_FIELDS } from './constants';
import { fromExpiryDate, isValidEmailAddress, toExpiryDate } from './utils';

interface Props {
  open: boolean;
  /** Present = edit that identity; the address is the key, so it cannot change. */
  initialIdentity?: GHABlockedIdentity | null;
  isSaving: boolean;
  submitError?: string | null;
  onClose: () => void;
  onSubmit: (value: string, input: GHABlockedIdentityDetails) => void;
}

interface FormState {
  email: string;
  reason: string;
  expiresOn: string;
  evidence: Record<keyof GHABlockedIdentityEvidence, string>;
}

const toFormState = (identity: GHABlockedIdentity | null | undefined): FormState => ({
  email: identity?.value ?? '',
  reason: identity?.reason ?? '',
  expiresOn: toExpiryDate(identity?.expiresAt ?? null),
  evidence: Object.fromEntries(
    GHA_BLOCKED_IDENTITY_EVIDENCE_FIELDS.map(({ key }) => [key, identity?.evidence?.[key] ?? '']),
  ) as FormState['evidence'],
});

export default function BlockedIdentityDialog({
  open,
  initialIdentity,
  isSaving,
  submitError,
  onClose,
  onSubmit,
}: Props) {
  const [form, setForm] = useState<FormState>(() => toFormState(initialIdentity));
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(toFormState(initialIdentity));
      setValidationError(null);
    }
  }, [open, initialIdentity]);

  const setEvidence = (key: keyof GHABlockedIdentityEvidence, value: string) =>
    setForm((current) => ({ ...current, evidence: { ...current.evidence, [key]: value } }));

  const handleSubmit = () => {
    const email = form.email.trim();
    if (!isValidEmailAddress(email)) {
      setValidationError('Enter a full email address, such as name@gmail.com');
      return;
    }
    setValidationError(null);
    const evidence: GHABlockedIdentityEvidence = {};
    for (const { key } of GHA_BLOCKED_IDENTITY_EVIDENCE_FIELDS) {
      const text = form.evidence[key].trim();
      if (text !== '') {
        evidence[key] = text;
      }
    }
    const reason = form.reason.trim();
    // A save replaces the whole row, so every field is sent; an empty one as null, which clears it.
    onSubmit(email, {
      reason: reason === '' ? null : reason,
      evidence: Object.keys(evidence).length === 0 ? null : evidence,
      expiresAt: fromExpiryDate(form.expiresOn),
    });
  };

  const isEdit = initialIdentity != null;
  const error = validationError ?? submitError;

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit blocked identity' : 'Block an identity'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            size="small"
            label="Email"
            placeholder="name@gmail.com"
            value={form.email}
            disabled={isEdit}
            onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
            helperText="Stored without plus tags (and Gmail dots, Yahoo -suffixes), so every variant of the address is blocked."
            fullWidth
          />
          <TextField
            size="small"
            label="Reason (optional)"
            placeholder="OTA test booking"
            value={form.reason}
            onChange={(event) => setForm((current) => ({ ...current, reason: event.target.value }))}
            inputProps={{ maxLength: 500 }}
            fullWidth
          />
          <TextField
            size="small"
            type="date"
            label="Expires on (optional)"
            value={form.expiresOn}
            onChange={(event) => setForm((current) => ({ ...current, expiresOn: event.target.value }))}
            InputLabelProps={{ shrink: true }}
            helperText="The block stops matching from this date. Leave empty to block indefinitely."
            fullWidth
          />
          <Typography variant="subtitle2">Evidence (optional)</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {GHA_BLOCKED_IDENTITY_EVIDENCE_FIELDS.map(({ key, label }) => (
              <TextField
                key={key}
                size="small"
                label={label}
                value={form.evidence[key]}
                onChange={(event) => setEvidence(key, event.target.value)}
              />
            ))}
          </Box>
          {error != null && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isSaving}>
          {isEdit ? 'Save' : 'Block'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
